import { useInfiniteQuery, useQuery } from 'react-query'

import { api } from '../../../api'
import { getValidFilters } from '../../../../utils/filters'
import { getQueryStrinfied } from '../../../../utils/query'
import { type FiltersDomain } from '../../../../types/domain/filters-domain'
import { type ExpertDomain } from '../../../../types/domain/expert-domain'
import type PaginationDomain from '../../../../types/domain/pagination-domain'

export const fetchExperts = async (
  filters: FiltersDomain
): Promise<PaginationDomain<ExpertDomain>> => {
  const query = getQueryStrinfied(getValidFilters(filters))
  const response = await api.get(`/experts?${query}`)
  return response.data
}

export const fetchExpert = async (id: number): Promise<ExpertDomain> => {
  const response = await api.get(`/experts/${id}`)
  return response.data
}

export const useExpertsQuery = (filters: FiltersDomain) => {
  return useQuery<PaginationDomain<ExpertDomain>>(
    ['experts', filters],
    () => fetchExperts(filters),
    { staleTime: 1000 * 60 * 60 * 1, keepPreviousData: true } // 1 hour
  )
}

export const useInfiniteExpertsQuery = (
  filters: FiltersDomain,
  options?: { enabled?: boolean },
) => {
  return useInfiniteQuery<PaginationDomain<ExpertDomain>>(
    ['experts', filters],
    ({ pageParam = 1 }) => fetchExperts({ ...filters, page: pageParam }),
    {
      enabled: options?.enabled !== false,
      staleTime: 1000 * 60 * 60 * 1, // 1 hour
      getNextPageParam({ meta }) {
        return meta.hasNextPage ? meta.page + 1 : undefined
      },
    }
  )
}

export const useExpertQuery = (id: number) => {
  return useQuery(['expert', id], () => fetchExpert(id), {
    staleTime: 1000 * 60 * 60 * 1,
    keepPreviousData: true,
  })
}
